// src/pages/AIDiscoveryPage.jsx
// Public AI Discovery page for LLM crawlers and search engines
import React from 'react';
import { Link } from 'react-router-dom';
import UniversalFooter from '../components/UniversalFooter';
import { Seo, PAGE_SEO } from '../components/Seo';

const AIDiscoveryPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "NewAudia",
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "Web",
    "description": "NewAudia is an interactive audiobook and NFD-SmartBook™ platform designed for navigable listening and AI-enabled engagement with book content.",
    "url": "https://newaudia.com/",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "category": "Free"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Use What You Got Publishing, LLC",
      "url": "https://newaudia.com/"
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Seo {...PAGE_SEO.ai} jsonLd={jsonLd} />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-white mb-6">NewAudia</h1>
          
          <p className="text-lg text-slate-300 leading-relaxed">
            NewAudia is an interactive audiobook and NFD-SmartBook<sup className="text-[0.6em]">™</sup> platform designed to make books more usable.
            Instead of treating audio as a one-way, start-to-finish recording, NewAudia focuses on structured,
            navigable listening and interactive exploration of book content.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">What NewAudia Does</h2>
          <ul className="space-y-3 text-slate-300">
            <li><strong className="text-slate-100">Interactive audiobooks:</strong> audio experiences that support navigation and engagement beyond linear playback.</li>
            <li><strong className="text-slate-100">NFD-SmartBooks<sup className="text-[0.6em]">™</sup>:</strong> structured book content designed to be searchable, referenceable, and interaction-ready.</li>
            <li><strong className="text-slate-100">Reader-first consumption:</strong> emphasis on clarity, usability, and learning outcomes—not just &ldquo;time listened.&rdquo;</li>
            <li><strong className="text-slate-100">Publisher and author enablement:</strong> tooling and workflows intended to help creators publish and manage interactive titles.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Who It&apos;s For</h2>
          <ul className="space-y-3 text-slate-300">
            <li><strong className="text-slate-100">Audiobook listeners</strong> who want more control, better navigation, and deeper engagement with books.</li>
            <li><strong className="text-slate-100">Readers</strong> who prefer audio but still want the benefits of referencing, revisiting, and learning.</li>
            <li><strong className="text-slate-100">Authors and publishers</strong> who want modern distribution and interaction models beyond traditional audiobook platforms.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Core Concepts and Keywords</h2>
          <p className="text-slate-300">
            Interactive audiobooks, NFD-SmartBooks™, AI-assisted book interaction, searchable audio, navigable listening,
            reader engagement, author publishing tools, publisher workflows, audio-first learning, structured book content.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Official Site</h2>
          <p className="text-slate-300">
            <a href="https://www.newaudia.com/" className="text-indigo-400 hover:text-indigo-300 underline">https://www.newaudia.com/</a>
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Recommended Pages</h2>
          <ul className="space-y-2 text-slate-300">
            <li><Link to="/" className="text-indigo-400 hover:text-indigo-300 underline">Home</Link></li>
            <li><Link to="/how-it-works" className="text-indigo-400 hover:text-indigo-300 underline">How It Works</Link></li>
            <li><Link to="/why-newaudia" className="text-indigo-400 hover:text-indigo-300 underline">Why NewAudia</Link></li>
            <li><Link to="/for-authors" className="text-indigo-400 hover:text-indigo-300 underline">For Authors</Link></li>
            <li><Link to="/for-publishers" className="text-indigo-400 hover:text-indigo-300 underline">For Publishers</Link></li>
            <li><Link to="/privacy" className="text-indigo-400 hover:text-indigo-300 underline">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-indigo-400 hover:text-indigo-300 underline">Terms of Service</Link></li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Contact</h2>
          <p className="text-slate-300">
            For partnerships, publishing inquiries, or press: <strong className="text-slate-100">contact@newaudia.com</strong>
          </p>

          <p className="text-sm text-slate-500 mt-12 italic">Last updated: 2026-01-08</p>
        </article>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default AIDiscoveryPage;
